import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import moment from 'moment'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import Pagination from '../../../../components/pagination/Pagination'
import FormLoader from '../../../../components/loader/FormLoader'
import { IntelligentCheck, IntelligentCheckBadge } from '../kyc/IntelligentCheck'
import { initials } from '../shipperIds/shared'
import { senderIdDisplayUrl } from '../../../../components/senderId/senderIdApi'
import '../kyc/KycReview.scss'

type CheckFilter = '' | 'pass' | 'flag' | 'fail' | 'unreviewed'
type Counts = { pass: number; flag: number; fail: number; unreviewed: number }

// mongoose-paginate fields the list uses
interface PageInfo {
  page: number
  limit: number
  totalPages: number
  totalDocs: number
}

interface ListResponse {
  success: boolean
  message: string
  data: PageInfo & { docs: SenderIdRow[]; counts: Counts }
}

interface SenderIdRow {
  _id: string
  trackingId: string | null
  senderName: string | null
  senderPhone: string | null
  /** Private reference (or legacy public URL) — compare checks against this. */
  senderIdImage: string
  /** Signed, expiring link for viewing the photo. */
  senderIdImageUrl?: string | null
  senderIdCheck: IntelligentCheck | null
  deliveryType?: string
  createdAt: string
  userId?: { firstname?: string; lastname?: string; email?: string } | null
}

const FILTERS: { key: CheckFilter; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'pass', label: 'Pass' },
  { key: 'flag', label: 'Flag' },
  { key: 'fail', label: 'Fail' },
  { key: 'unreviewed', label: 'Unreviewed' },
]

// Same pair the API allows (adminAuth 'master'), like the KYC queue.
const canViewSenderIdChecks = (profile?: { role?: string } | null) =>
  profile?.role === 'super admin' || profile?.role === 'customer service'

const accountHolder = (row: SenderIdRow) =>
  [row.userId?.firstname, row.userId?.lastname].filter(Boolean).join(' ') || '—'

// A result about a previously attached image isn't this image's result.
const currentCheck = (row: SenderIdRow) =>
  row.senderIdCheck &&
  (!row.senderIdCheck.source || row.senderIdCheck.source === row.senderIdImage)
    ? row.senderIdCheck
    : null

const SenderIdChecks = () => {
  const navigate = useNavigate()
  const { profile, notify } = useGlobalContext()

  const [filter, setFilter] = useState<CheckFilter>('')
  const [rows, setRows] = useState<SenderIdRow[]>([])
  const [counts, setCounts] = useState<Counts | null>(null)
  const [pagination, setPagination] = useState<PageInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const canView = canViewSenderIdChecks(profile)

  const fetchRows = (page = 1, limit = 20) => {
    setLoading(true)
    let url = `admin/bookings/sender-id-checks?page=${page}&limit=${limit}`
    if (filter) url += `&check=${filter}`
    get(url)
      .then((res: ListResponse) => {
        if (res.success) {
          const { docs, counts: c, ...rest } = res.data
          setRows(docs)
          setCounts(c)
          setPagination(rest)
        } else notify('error', res.message)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (!canView) return
    fetchRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  if (!canView) {
    return (
      <div className='kyc-review'>
        <div className='restricted'>
          <Icon icon='mdi:shield-lock' />
          <h1>Restricted</h1>
          <p>Sender ID checks are only available to super admins and customer service.</p>
        </div>
      </div>
    )
  }

  const openBooking = (row: SenderIdRow) => navigate(`/admin/booking/${row._id}`)

  return (
    <div className='kyc-review'>
      <div className='hero'>
        <div className='hero-text'>
          <h1>Sender ID Checks</h1>
          <p>
            The third-party sender's government ID on bookings where the sender
            isn't the account holder. Intelligent checks are advisory — they
            never block or cancel a booking.
          </p>
        </div>
      </div>

      <div className='header-tabs'>
        {FILTERS.map((f) => {
          const count = f.key && counts ? counts[f.key] : null
          return (
            <button
              key={f.key || 'all'}
              className={filter === f.key ? 'active' : ''}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {count !== null && <span className='count'>{count}</span>}
            </button>
          )
        })}
      </div>

      <div className='queue-card'>
        {loading ? (
          <FormLoader position='center' />
        ) : rows.length === 0 ? (
          <div className='empty-state'>
            <Icon icon='mdi:card-account-details-outline' />
            <h3>Nothing here</h3>
            <p>
              {filter
                ? 'No bookings with a sender ID match this filter.'
                : 'Bookings appear here once a third-party sender ID is attached.'}
            </p>
          </div>
        ) : (
          <>
            <div className='table-wrap desktop-table'>
              <table>
                <thead>
                  <tr>
                    <th>Sender</th>
                    <th>Account holder</th>
                    <th>Tracking ID</th>
                    <th>Type</th>
                    <th>Booked</th>
                    <th>Intelligent check</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row._id}
                      onClick={() => openBooking(row)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className='customer-cell'>
                          <div className='avatar'>{initials(row.senderName || '')}</div>
                          <div>
                            <p className='name'>{row.senderName || '—'}</p>
                            {row.senderPhone && <p className='email'>{row.senderPhone}</p>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <p className='name'>{accountHolder(row)}</p>
                        {row.userId?.email && <p className='email'>{row.userId.email}</p>}
                      </td>
                      <td>
                        <span className='mono'>{row.trackingId || '—'}</span>
                      </td>
                      <td>{row.deliveryType || '—'}</td>
                      <td>{moment(row.createdAt).format('D MMM YYYY, h:mma')}</td>
                      <td>
                        {currentCheck(row) ? (
                          <IntelligentCheckBadge review={currentCheck(row)} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className='icons' onClick={(e) => e.stopPropagation()}>
                        {senderIdDisplayUrl(row.senderIdImage, row.senderIdImageUrl) && (
                          <a
                            className='review-btn'
                            href={senderIdDisplayUrl(row.senderIdImage, row.senderIdImageUrl) || undefined}
                            target='_blank'
                            rel='noreferrer'
                            title='Open the ID photo (link expires after 10 minutes — reload the list for a fresh one)'
                          >
                            Photo
                          </a>
                        )}
                        <button className='review-btn' onClick={() => openBooking(row)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='mobile-cards'>
              {rows.map((row) => (
                <div className='card' key={row._id} onClick={() => openBooking(row)}>
                  <div className='row'>
                    <p className='name'>{row.senderName || '—'}</p>
                    {currentCheck(row) && (
                      <IntelligentCheckBadge review={currentCheck(row)} />
                    )}
                  </div>
                  <div className='row'>
                    <p>
                      For {accountHolder(row)} · {row.deliveryType || '—'}
                    </p>
                  </div>
                  <div className='row'>
                    <p>
                      <span className='mono'>{row.trackingId || '—'}</span> ·{' '}
                      {moment(row.createdAt).format('D MMM YYYY')}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {pagination && (
              <Pagination
                currentPage={pagination.page}
                totalCount={pagination.totalPages}
                pageSize={pagination.limit}
                onPageChange={(page: number, pageSize: number) => fetchRows(page, pageSize)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SenderIdChecks
